"use client";
import React, {
  useCallback,
  useState,
  forwardRef,
  useEffect,
  useMemo,
} from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { cn } from "@/lib/utils";
import { ChevronDown, CheckIcon, Globe, Search } from "lucide-react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import countriesJson from "@/utils/countries.json";

export interface Country {
  alpha2: string;
  alpha3: string;
  countryCallingCodes: string[];
  currencies: string[];
  emoji?: string;
  ioc: string;
  languages: string[];
  name: string;
  status: string;
  type?: string;
  countries?: string[];
  regionKind?: string;
  regionType?: string;
}

interface CountryDropdownProps {
  onChange?: (country: Country) => void;
  defaultValue?: string;
  disabled?: boolean;
  placeholder?: string;
  slim?: boolean;
  phone?: boolean;
}

const CountryDropdownComponent = (
  {
    onChange,
    defaultValue,
    disabled = false,
    placeholder,
    slim = false,
    phone,
    ...props
  }: CountryDropdownProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(
    undefined
  );
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<string | null>(null);

  const regionDisplayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames(["pt-BR"], { type: "region" });
    } catch {
      return null;
    }
  }, []);

  const normalizeForSearch = useCallback((value: string) => {
    return String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  }, []);

  const getCountryNameParts = useCallback((rawName: string) => {
    const parts = String(rawName || "")
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      primary: parts[0] ?? rawName,
      secondary: parts[1] ?? "",
    };
  }, []);

  const getPtCountryName = useCallback(
    (alpha2: string) => {
      const code = String(alpha2 || "").toUpperCase();
      return regionDisplayNames?.of(code) ?? "";
    },
    [regionDisplayNames]
  );

  const getDisplayName = useCallback(
    (country: Country) => {
      const ptName = getPtCountryName(country.alpha2);
      const { primary, secondary } = getCountryNameParts(country.name);

      // Preferimos exibir a label como "EN | PT" quando possível.
      // primary geralmente é EN vindo do JSON (ex.: "Germany"), e ptName vem do Intl.DisplayNames (ex.: "Alemanha").
      if (primary && ptName) return `${primary} | ${ptName}`;

      // Fallbacks: se não houver ptName, usa o que veio do JSON (EN | nativo) ou o primary.
      return secondary ? `${primary} | ${secondary}` : primary || country.name;
    },
    [getCountryNameParts, getPtCountryName]
  );

  type CountryOption = Country & { _searchValue: string };

  const availableCountries = useMemo((): CountryOption[] => {
    const allCountries = countriesJson as Country[];

    return allCountries
      .filter(
        (country: Country) =>
          country.type !== "region" &&
          country.emoji &&
          country.status !== "deleted" &&
          country.ioc !== "PRK"
      )
      .map((country: Country) => ({
        ...country,
        currencies:
          country.currencies.length > 0 ? country.currencies : ["USD"],
        _searchValue: [
          getDisplayName(country),
          country.name,
          country.alpha2,
          country.alpha3,
          ...(country.countryCallingCodes ?? []),
        ]
          .filter(Boolean)
          .join(" "),
      }));
  }, [getDisplayName]);

  useEffect(() => {
    if (defaultValue) {
      const initialCountry = availableCountries.find(
        (country) => country.alpha2 === defaultValue.toUpperCase()
      );
      if (initialCountry) {
        setSelectedCountry(initialCountry);
      } else {
        setSelectedCountry(undefined);
      }
    } else {
      setSelectedCountry(undefined);
    }
  }, [defaultValue, availableCountries]);

  const computePopoverWidth = useCallback(() => {
    const triggerWidth = triggerRef.current?.offsetWidth ?? 0;
    const viewportWidth = window.innerWidth;

    const idealWidth = Math.max(triggerWidth, 200);
    const maxAllowedWidth = viewportWidth * 0.9;
    return `${Math.min(idealWidth, maxAllowedWidth)}px`;
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        // Mede antes de renderizar o PopoverContent para evitar "pular" na 1ª abertura
        setPopoverWidth(computePopoverWidth());
      } else {
        setSearchQuery("");
      }
      setOpen(nextOpen);
    },
    [computePopoverWidth],
  );

  const handleSelect = useCallback(
    (country: Country) => {
      setSelectedCountry(country);
      onChange?.(country);
      setOpen(false);
    },
    [onChange]
  );

  const filteredCountries = useMemo(() => {
    const needle = normalizeForSearch(searchQuery);
    if (!needle) return availableCountries;
    return availableCountries.filter((option) =>
      normalizeForSearch(option._searchValue).includes(needle)
    );
  }, [availableCountries, normalizeForSearch, searchQuery]);

  const triggerClasses = cn(
    "flex h-[40px] w-full items-center justify-between line-camp-1 rounded-md border border-input bg-transparent px-1.5 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-bgDisabled [&>span]:line-clamp-1",
    slim && "min-w-[90px] w-auto",
    phone && "border-none shadow-none focus:ring-0 focus:outline-none"
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        ref={(node) => {
          triggerRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
        }}
        className={triggerClasses}
        disabled={disabled}
        data-cy='country-select'
        {...props}
      >
        {selectedCountry ? (
          <div className='flex items-center flex-grow w-0 gap-2 overflow-hidden'>
            <div className='inline-flex items-center justify-center w-5 h-5 shrink-0 overflow-hidden rounded-full gap-2'>
              <CircleFlag
                countryCode={selectedCountry.alpha2.toLowerCase()}
                height={20}
              />
            </div>
            {phone && <span>{selectedCountry.countryCallingCodes[0]}</span>}
            {slim === false && (
              <span className='overflow-hidden text-ellipsis whitespace-nowrap'>
                {getDisplayName(selectedCountry)}
              </span>
            )}
          </div>
        ) : (
          <span>
            {slim === false ? placeholder ?? '' : <Globe size={20} />}
          </span>
        )}
        <ChevronDown size={16} />
      </PopoverTrigger>
      {open && (
        <PopoverContent
          className="p-0"
          align="start"
          side="bottom"
          sideOffset={4}
          collisionPadding={10}
          forceMount
          style={{
            width: popoverWidth ?? "var(--radix-popper-anchor-width)",
            minWidth: "200px",
            maxWidth: "90vw",
            transform: "translateZ(0)",
            isolation: "isolate",
          }}
        >
          <div className="flex flex-col border border-colorBorder rounded-md bg-popover text-popover-foreground">
            <div className="flex items-center px-3 border-b border-colorBorder">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                placeholder={`${t("phone_search_placeholder")}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                data-cy="country-search-input"
              />
            </div>

            <div
              ref={scrollRef}
              className="scroll-container"
              style={{
                minHeight: "auto",
                maxHeight: "270px",
                overflowY: "scroll",
                overflowX: "hidden",
                position: "relative",
                isolation: "isolate",
                contain: "layout style",
                scrollBehavior: "smooth",
                scrollbarWidth: "thin",
                scrollbarColor: "#888 #f1f1f1",
              }}
              onWheel={(e) => {
                e.stopPropagation();
              }}
            >
              {filteredCountries.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground py-8">
                  {t("emptyOption")}
                </div>
              ) : (
                <div className="p-1">
                  {filteredCountries.map((option, index) => (
                    <div
                      key={`${option.alpha2}-${index}`}
                      onClick={() => handleSelect(option)}
                      className={cn(
                        "relative flex cursor-pointer gap-2 select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        "active:bg-accent active:text-accent-foreground",
                        "min-h-[36px] flex-shrink-0",
                        "sm:py-1.5 sm:min-h-[32px]"
                      )}
                      role="option"
                      aria-selected={option.alpha2 === selectedCountry?.alpha2}
                      data-cy="country-option"
                      data-index={index}
                    >
                      <div className="inline-flex items-center justify-center w-5 h-5 shrink-0 overflow-hidden rounded-full">
                        <CircleFlag
                          countryCode={option.alpha2.toLowerCase()}
                          height={20}
                        />
                      </div>

                      <span
                        className="flex-1 text-left break-words"
                        title={getDisplayName(option)}
                      >
                        {getDisplayName(option)}{" "}
                        {phone && option.countryCallingCodes[0]}
                      </span>

                      <CheckIcon
                        className={cn(
                          "ml-auto h-4 w-4 shrink-0",
                          option.alpha2 === selectedCountry?.alpha2
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                    </div>
                  ))}
                  <div className="h-2" />
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

CountryDropdownComponent.displayName = "CountryDropdownComponent";

export const CountryDropdown = forwardRef(CountryDropdownComponent);